import { FC, useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";

import { userState } from "../../store/user";

import SectionHeader from "../Layout/SectionHeader";

import classes from "./TopicLeaderboard.module.css";
import { ArtworkModelDocument } from "../gallery/Gallery";
import GlobalAppLoading from "../UI/GlobalAppLoading";
import { getLang, i18n } from "../../i18n";

interface Props {
  setPopupImage: React.Dispatch<React.SetStateAction<string>>;
}

const TopicsLeaderBoard: FC<Props> = ({ setPopupImage }) => {
  const lang = getLang();
  const t = i18n.quizLeaderboard[lang];

  const API_URL = process.env.REACT_APP_API_URL;
  const user = useSelector(userState);
  const [topic, setTopic] = useState<{
    title: string;
    question: string;
    sponsor: string;
  } | null>(null);
  const [quiz, setQuiz] = useState("");
  const [artworks, setArtworks] = useState<
    | {
        totalScore: number;
        wins: number;
        artwork: ArtworkModelDocument;
      }[]
    | []
  >([]);
  const [screenIsLoading, setScreenIsLoading] = useState(true);
  useEffect(() => {
    console.log("topics leaderboard use effect");
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const queryQuiz = params.get("quiz");
    if (queryQuiz) {
      setQuiz(queryQuiz);
    }

    const getQuizLeaderboard = async (quiz: string) => {
      setScreenIsLoading(true);
      await axios
        .get(`${API_URL}/topics/leaderboard/${quiz}`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${user.sessionToken}` },
        })
        .then((response) => {
          setArtworks(response.data.data.artworks);
          setTopic(response.data.data.topic);
          setScreenIsLoading(false);
        })
        .catch((error) => {
          console.log(error);
        });
    };

    if (queryQuiz) getQuizLeaderboard(queryQuiz);
  }, [quiz]);

  if (screenIsLoading)
    return (
      <GlobalAppLoading text="Getting Quiz leaderboard. Please wait for a second..." />
    );
  return (
    <>
      <SectionHeader label={t.label} back="/quizzes" />
      <section className={classes.leaderboard}>
        {topic && (
          <div className={classes.topic}>
            <h2>{topic.title}</h2>
            <span className={classes.sponsor}>
              {t.by} {topic.sponsor ? topic.sponsor : t.rater}
            </span>
            <span className={classes.question}>{topic.question}</span>
          </div>
        )}
        <div className={`${classes.list} ${classes.arts}`}>
          {artworks.map((art, index) => (
            <div
              key={art.artwork._id}
              className={`${classes.art} ${
                art.artwork.userID === user._id ? classes.self : ""
              }`}
            >
              <div className={classes.place}>{index + 1}</div>
              <div
                className={classes.img}
                onClick={() => setPopupImage(art.artwork._id)}
              >
                <span className={classes.popupIcon}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    viewBox="0 0 509 511.54"
                  >
                    <path
                      fillRule="nonzero"
                      fill="#fff"
                      d="M447.19 347.03c0-17.06 13.85-30.91 30.91-30.91 17.05 0 30.9 13.85 30.9 30.91v87.82c0 21.08-8.63 40.29-22.51 54.18-13.88 13.88-33.09 22.51-54.18 22.51H76.69c-21.09 0-40.3-8.63-54.18-22.51C8.63 475.14 0 455.93 0 434.85V76.69c0-21.09 8.63-40.3 22.51-54.18C36.39 8.63 55.6 0 76.69 0h86.98c17.06 0 30.9 13.85 30.9 30.9 0 17.06-13.84 30.91-30.9 30.91H76.69c-4.07 0-7.82 1.69-10.51 4.37-2.68 2.69-4.37 6.44-4.37 10.51v358.16c0 4.06 1.69 7.82 4.37 10.5 2.69 2.68 6.44 4.38 10.51 4.38h355.62c4.07 0 7.82-1.7 10.51-4.38 2.68-2.68 4.37-6.44 4.37-10.5v-87.82zm0-243.56L308.15 244.28c-11.91 12.12-31.45 12.28-43.56.37-12.11-11.91-12.28-31.45-.37-43.56L401.77 61.81H309.7c-17.06 0-30.9-13.85-30.9-30.91 0-17.05 13.84-30.9 30.9-30.9h168.4C495.15 0 509 13.85 509 30.9v165.04c0 17.06-13.85 30.9-30.9 30.9-17.06 0-30.91-13.84-30.91-30.9v-92.47z"
                    />
                  </svg>
                </span>
                <img
                  src={
                    art.artwork.imageAmazonS3ThumbURL ||
                    art.artwork.imageAmazonS3URL
                  }
                  alt=""
                />
              </div>
              <div className={classes.info}>
                <div className={classes.artTop}>
                  <div className={classes.level}>
                    {/* Repeat star SVGs for art.artwork.level */}
                  </div>
                  <div className={classes.profit}>
                    {Math.round(art.totalScore)} 🌕
                  </div>
                </div>
                <div
                  className={`${classes.title} ${
                    (art.artwork.silentVotesCount > 0 ||
                      art.artwork.battleVotesCount > 0) &&
                    (art.artwork.silentVotesWinner +
                      art.artwork.battleVotesWinner) /
                      (art.artwork.silentVotesCount +
                        art.artwork.battleVotesCount) >=
                      0.7
                      ? classes.gold
                      : (art.artwork.silentVotesCount > 0 ||
                          art.artwork.battleVotesCount > 0) &&
                        (art.artwork.silentVotesWinner +
                          art.artwork.battleVotesWinner) /
                          (art.artwork.silentVotesCount +
                            art.artwork.battleVotesCount) >=
                          0.5
                      ? classes.silver
                      : classes.bronze
                  }`}
                >
                  {art.artwork.title}
                </div>
                {art.wins > 0 && (
                  <div className={classes.winrate}>
                    {t.wins}&nbsp;<span>{art.wins}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
};

export default TopicsLeaderBoard;
